import {ANALYTICS_EVENTS} from '@packages/systems/core/constants/analytics';
import {
  CHECKLIST,
  CONTEXTUAL_EVENT_KEYS,
  DISPLAY_SETTINGS_VIDEO_TUTORIAL_ID,
  QUICK_STACK_VIDEO_TUTORIAL_ID,
} from './constants';
import {WeakObject, type ContextualEventConfig} from './types';

const HEADER_FOOTER_PREBUILT_LAYOUTS = [
  'navbar',
  'layout-sticky-nav',
  'layout-footer',
  'layout-navbar-light',
  'layout-navbar-dark',
  'layout-footer-simple',
  'layout-footer-wide',
  'layout-footer-with-links',

  // v3 layouts
  'layout-navbar-logo-center',
  'layout-navbar-logo-left',
  'layout-navbar-no-shadow',
];

const hasInstalledFirstLibrary = (user: WeakObject) =>
  Boolean(user.persistentUIState.hasInstalledFirstLibrary);

const siteIsFromTemplate = (site: WeakObject) => Boolean(site.templateOf);

const isBlankSite = (site: WeakObject) => !siteIsFromTemplate(site);

export const CONTEXTUAL_EVENTS: ContextualEventConfig[] = [
  {
    id: CONTEXTUAL_EVENT_KEYS.NON_HEADER_FOOTER_PBL,
    analyticsEventListener: ANALYTICS_EVENTS.ELEMENT_ADDED,
    siteHas: isBlankSite,
    payloadHas: (payload) =>
      payload.type === 'prebuilt-layout' &&
      !HEADER_FOOTER_PREBUILT_LAYOUTS.includes(payload.name),
  },

  {
    id: CONTEXTUAL_EVENT_KEYS.HEADER_FOOTER_SELECTED,
    analyticsEventListener: ANALYTICS_EVENTS.NODE_CLICKED,
    siteHas: isBlankSite,
    payloadHas: (payload) => payload.type && payload.type.includes('Navbar'),
  },

  {
    id: CONTEXTUAL_EVENT_KEYS.CLASS_CREATED,
    analyticsEventListener: ANALYTICS_EVENTS.CLASS_CREATED,
  },
  {
    id: CONTEXTUAL_EVENT_KEYS.CLASS_UPDATED,
    analyticsEventListener: ANALYTICS_EVENTS.CLASS_UPDATED,
  },

  {
    id: CONTEXTUAL_EVENT_KEYS.DESIGNER_SETTINGS_PANEL_OPENED,
    analyticsEventListener: ANALYTICS_EVENTS.DESIGNER_SETTINGS_PANEL_OPENED,
  },

  {
    id: CONTEXTUAL_EVENT_KEYS.ELEMENT_ADDED,
    analyticsEventListener: ANALYTICS_EVENTS.ELEMENT_ADDED,
  },

  {
    id: CONTEXTUAL_EVENT_KEYS.QS_ADDED,
    analyticsEventListener: ANALYTICS_EVENTS.ELEMENT_ADDED,
    payloadHas: (payload) =>
      payload.name && payload.name.includes('quick-stack'),
  },

  {
    id: CONTEXTUAL_EVENT_KEYS.ASSETS_UPLOADED,
    analyticsEventListener: ANALYTICS_EVENTS.ASSETS_UPLOADED,
  },

  {
    id: CONTEXTUAL_EVENT_KEYS.VIDEO_TUTORIALS_TRIGGERED,
    analyticsEventListener: ANALYTICS_EVENTS.VIDEO_TUTORIALS_PANEL_VIEWED,
  },

  {
    id: CONTEXTUAL_EVENT_KEYS.NON_SPECIAL_SECTION_CLICKED,
    analyticsEventListener: ANALYTICS_EVENTS.NODE_CLICKED,
    siteHas: siteIsFromTemplate,
    payloadHas: (payload) => payload.type === 'Section',
  },
  {
    id: CONTEXTUAL_EVENT_KEYS.SYMBOL_CLICKED_TEMPLATE,
    analyticsEventListener: ANALYTICS_EVENTS.NODE_CLICKED,
    siteHas: siteIsFromTemplate,
    payloadHas: (payload) => payload.type === 'symbol',
  },

  {
    id: CONTEXTUAL_EVENT_KEYS.DISPLAY_SETTINGS_VIDEO_TUTORIAL_TRIGGERED,
    analyticsEventListener: ANALYTICS_EVENTS.VIDEO_TUTORIAL_SELECTED,
    payloadHas: (payload) =>
      payload.videoTutorial &&
      payload.videoTutorial === DISPLAY_SETTINGS_VIDEO_TUTORIAL_ID &&
      payload.videoTutorialTriggeredFrom &&
      payload.videoTutorialTriggeredFrom === CHECKLIST,
  },
  {
    id: CONTEXTUAL_EVENT_KEYS.QUICK_STACK_VIDEO_TUTORIAL_TRIGGERED,
    analyticsEventListener: ANALYTICS_EVENTS.VIDEO_TUTORIAL_SELECTED,
    payloadHas: (payload) =>
      payload.videoTutorial &&
      payload.videoTutorial === QUICK_STACK_VIDEO_TUTORIAL_ID &&
      payload.videoTutorialTriggeredFrom &&
      payload.videoTutorialTriggeredFrom === CHECKLIST,
  },
  {
    id: CONTEXTUAL_EVENT_KEYS.DESIGNER_OPENED_WITH_LIBRARY_INSTALLED,
    analyticsEventListener: ANALYTICS_EVENTS.DESIGNER_OPENED,
    userHas: hasInstalledFirstLibrary,
  },
  {
    id: CONTEXTUAL_EVENT_KEYS.NINE_BOX_FLEX_TUTORIAL_TRIGGERED,
    analyticsEventListener: ANALYTICS_EVENTS.NINE_BOX_FLEX_CLICKED,
  },
  {
    id: CONTEXTUAL_EVENT_KEYS.NINE_BOX_GRID_TUTORIAL_TRIGGERED,
    analyticsEventListener: ANALYTICS_EVENTS.NINE_BOX_GRID_CLICKED,
  },
  {
    id: CONTEXTUAL_EVENT_KEYS.AISG_THEME_CLICKED,
    analyticsEventListener: ANALYTICS_EVENTS.AISG_THEME_CLICKED,
  },
  {
    id: CONTEXTUAL_EVENT_KEYS.PREVIEW_SITE_TUTORIAL_TRIGGERED,
    analyticsEventListener: ANALYTICS_EVENTS.WORKFLOW_ENTERING_PREVIEW,
  },

  {
    id: CONTEXTUAL_EVENT_KEYS.AISG_FTU_TUTORIAL_TRIGGERED,
    analyticsEventListener:
      ANALYTICS_EVENTS.AISG_FTU_PREVIEW_START_BUILDING_CLICKED,
    payloadHas: (payload) => payload.showExistingUserTutorial === true,
  },
  {
    id: CONTEXTUAL_EVENT_KEYS.AISG_HOVERED_SECTION_CONTROLS_CLICKED,
    analyticsEventListener:
      ANALYTICS_EVENTS.AISG_HOVERED_SECTION_CONTROLS_CLICKED,
  },
  {
    id: CONTEXTUAL_EVENT_KEYS.REALTIME_ONBOARDING_VIEWED,
    analyticsEventListener: ANALYTICS_EVENTS.REALTIME_ONBOARDING_VIEWED,
  },
  {
    id: CONTEXTUAL_EVENT_KEYS.ORION_SYNC_STYLES_TRIGGERED,
    analyticsEventListener: ANALYTICS_EVENTS.ORION_SYNC_STYLES_TRIGGERED,
  },
  {
    id: CONTEXTUAL_EVENT_KEYS.ORION_ADD_CONTEXT_POPOVER_TRIGGERED,
    analyticsEventListener:
      ANALYTICS_EVENTS.ORION_ADD_CONTEXT_POPOVER_TRIGGERED,
  },
  {
    id: CONTEXTUAL_EVENT_KEYS.AIDA_ELEMENT_ADDED_TO_CANVAS,
    analyticsEventListener: ANALYTICS_EVENTS.AIDA_ELEMENT_ADDED_TO_CANVAS,
  },
  {
    id: CONTEXTUAL_EVENT_KEYS.ORION_SITE_GENERATE_PAGE_TUTORIAL_TRIGGERED,
    analyticsEventListener:
      ANALYTICS_EVENTS.AISG_PAGE_GENERATION_MODAL_CTA_CLICKED,
  },
  {
    id: CONTEXTUAL_EVENT_KEYS.ORION_SITE_PUBLISH_TUTORIAL_TRIGGERED,
    analyticsEventListener: ANALYTICS_EVENTS.WORKFLOW_ENTERING_PREVIEW,
  },
  {
    id: CONTEXTUAL_EVENT_KEYS.PAGES_PANEL_VIEWED,
    analyticsEventListener: ANALYTICS_EVENTS.CREATE_PAGE_CALLOUT_VIEWED,
  },
];

export const CONTEXTUAL_EVENTS_LOOKUP: {
  [key: string]: ContextualEventConfig;
} = CONTEXTUAL_EVENTS.reduce<Record<string, any>>(
  (lookup, item) => ({...lookup, [item.id]: item}),
  {}
);
